// A drawing program that fails to build is tried again once, reported, and its object told (engine-wide, resort-agnostic).
//
// three r186 never draws a render pipeline again once it failed to build, and says so only in the console. On WebGPU an
// async compile that rejects leaves it "not ready" for good, and a sync one whose error scope reports gets `error`,
// which Backend.draw skips; on the WebGL2 backend a program that did not link is used anyway (GL errors, nothing
// drawn). The pipeline stays in three's cache under its key, so every object of that material is missing for the
// whole visit, with no report: a Samsung phone drew the horses' saddles, bridles, reins and riders, and no horse.
// Phones fail where a computer does not: their per-stage limits are lower (16 textures, 16 vertex inputs, fewer
// varyings; some drivers count gl_VertexID as an input: "Too many attributes (gl_VertexID)"), and a compiler can give
// up under memory pressure.
//
// watchPrograms(renderer, { report }) patches the one place every draw asks (Pipelines.isReady):
//  - the first failure of a page is reported (report(what, err, { why }): degraded 'drawing', the object and material
//    names, and `why` = three's own reason, read off its console line (MAPMYSTAY-VIEWER-22 said only "did not build" for
//    7 maps; the console had said "Vertex input variable ... location (16) exceeds the maximum (16)" every time);
//  - a failed pipeline leaves three's cache once, RETRY_MS after it was seen (a compiler that gave up for a moment
//    builds it the next time), and every object that drew with it builds it again at its next draw;
//  - when the new one fails too, the object's owner is told once: object.userData.onProgramFailed(material) puts a
//    plainer material in its place (instancing.js: a herd's plain coat), so a part is drawn plainer, never missing.
// Checks: tests/unit/runtime/programs.test.mjs (the retry, the report, the owner's call, against three's own Pipelines).
import { pipelines, usable } from './internals.js'; // VIS-194: three's private parts, checked at start-up

export const RETRY_MS = 1000;
const failedGl = new WeakMap(); // WebGL2 programs that did not link -> the linker's words
const reasons = new Map(); // WebGPU: three's pipeline label (renderPipeline_<material name>_<material id>) -> why it did not build
let listening = false;
/** three's console line for a pipeline that did not build, kept by its label (the console is three's only word on it). */
function listen() {
  if (listening) return;
  listening = true;
  const error = console.error;
  console.error = function (...a) {
    const m = /pipeline creation failed \((renderPipeline_[^)]*)\):\s*([^]*)/.exec(String(a[0] ?? ''));
    if (m) reasons.set(m[1], whyOf(m[2]));
    return error.apply(this, a);
  };
}
/** The telling line of a compiler's or validator's words: the first item of its list, else its first line. */
export function whyOf(text) {
  const lines = String(text || '').split('\n').map((s) => s.trim()).filter(Boolean);
  return (lines.find((s) => s.startsWith('- ')) || lines[0] || '').replace(/^- /, '').slice(0, 120);
}

/**
 * @param {any} renderer three's WebGPURenderer (either backend)
 * @param {{ report?: (what: string, err: Error, facts: { why: string }) => void, now?: () => number }} [o]
 * @returns {boolean} installed
 */
export function watchPrograms(renderer, { report = () => {}, now = () => performance.now() } = {}) {
  const P = pipelines(renderer);
  if (!P || P._visPrograms || !usable('Pipelines.caches')) return false;
  P._visPrograms = true;
  if (renderer.backend?.isWebGLBackend) { // three calls this instead of its own console line for a program that did not link
    renderer.debug.onShaderError = (gl, program, vs, fs) => {
      const log = [gl.getProgramInfoLog(program), gl.getShaderInfoLog(vs), gl.getShaderInfoLog(fs)].map((s) => String(s || '').trim()).filter(Boolean).join(' | ') || '(no log)';
      failedGl.set(program, log);
      console.error('[programs] a WebGL2 program did not link:', log);
    };
  } else listen();
  const seen = new Map(); // pipeline cache key -> { at: first seen failing, retried }
  const told = new WeakMap(); // object -> materials its owner was told about
  let reported = false;
  const isReady = P.isReady;
  P.isReady = function (ro) {
    const pipe = this.get(ro).pipeline, d = pipe && this.backend.get(pipe);
    if (d && (d.error === true || (d.pipeline && failedGl.has(d.pipeline)))) { failed(this, ro, pipe, d); return false; }
    return isReady.call(this, ro);
  };
  /** three's words on why this pipeline did not build (the WebGL2 linker's log, or the console line kept by listen()). */
  const why = (ro, d) => (d.pipeline && failedGl.get(d.pipeline)) || reasons.get(`renderPipeline_${ro.material?.name || ro.material?.type}_${ro.material?.id}`) || '';
  /** This object builds its pipeline again at its next draw (three's own update path: no pipeline = needs one). */
  function rebuild(Pp, ro, pipe) {
    const data = Pp.get(ro);
    if (data.pipeline !== pipe) return;
    pipe.usedTimes--; pipe.vertexProgram.usedTimes--; pipe.fragmentProgram.usedTimes--; // (the programs stay: the new pipeline takes them)
    data.pipeline = undefined;
  }
  function failed(Pp, ro, pipe, d) {
    const key = pipe.cacheKey;
    if (Pp.caches.get(key) !== pipe) { rebuild(Pp, ro, pipe); return; } // its key holds a newer try: take that one
    let s = seen.get(key);
    if (!s) {
      seen.set(key, (s = { at: now(), retried: false }));
      if (!reported) { reported = true; report(nameOf(ro), new Error(`${nameOf(ro)}: its drawing program did not build on this device (${renderer.backend?.isWebGLBackend ? 'WebGL2' : 'WebGPU'})`), { why: whyOf(why(ro, d)) }); }
    }
    if (!s.retried) {
      if (now() - s.at < RETRY_MS) return;
      s.retried = true;
      Pp.caches.delete(key);
      rebuild(Pp, ro, pipe);
      return;
    }
    const o = ro.object, mats = told.get(o) || told.set(o, new WeakSet()).get(o);
    if (mats.has(ro.material)) return;
    mats.add(ro.material);
    try { o.userData?.onProgramFailed?.(ro.material); } catch (err) { console.warn('[programs] the plainer stand-in failed', err); }
  }
  return true;
}

/** The object and material, as a report names them (no ids: one report kind however many objects). */
export const nameOf = (ro) => `${ro.object?.name || ro.object?.type || 'object'} / ${ro.material?.name || ro.material?.type || 'material'}`;

const BUILDS = Symbol('builds');
/**
 * A fresh build of this node material's program at its next draw, nothing else of it changed: three keeps a program by
 * its cache key, so a count in that key that goes up gives it a new one (and new bindings; the same shader text reuses
 * the pipeline). Composes with the keys other systems add (gpu/lighting.js, gpu/chain.js wrap the one they find).
 * main.js: the ground surfaces once a night (Apple's WebGPU: a first build that drew without the moon's light).
 * @param {any} m a node material @returns {boolean} applied
 */
export function rebuildProgram(m) {
  if (!m?.isNodeMaterial) return false;
  if (!(BUILDS in m)) {
    const base = m.customProgramCacheKey;
    Object.defineProperty(m, BUILDS, { value: 0, writable: true, enumerable: false });
    Object.defineProperty(m, 'customProgramCacheKey', { configurable: true, writable: true, enumerable: false, value() { return `${base.call(this)}|rb${this[BUILDS]}`; } });
  }
  m[BUILDS]++;
  m.needsUpdate = true;
  return true;
}
